import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import {
  createTenant,
  plansQueryOptions,
  type OnboardingSession,
} from '../../lib/queries/onboarding';
import { Button } from '../Button';

const step1Schema = z.object({
  companyName: z.string().min(1, 'Required'),
  netsuiteAccountId: z.string().min(1, 'Required'),
  primaryContact: z.string().min(1, 'Required'),
  contactEmail: z.string().email('Invalid email'),
  planId: z.string().min(1, 'Required'),
  credentialMode: z.enum(['platform_managed', 'byok']),
});

type Step1Values = z.infer<typeof step1Schema>;

export default function Step1Info({
  onNext,
}: {
  onNext: (session: OnboardingSession) => void;
}) {
  const { data: plans = [] } = useQuery(plansQueryOptions());
  const mutation = useMutation({ mutationFn: createTenant });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Step1Values>({
    resolver: zodResolver(step1Schema),
  });

  const onSubmit = (values: Step1Values) => {
    mutation.mutate(values, { onSuccess: onNext });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-primary text-base font-semibold">
            Tenant information
          </h2>
          <p className="text-muted mt-1 text-sm">
            Basic details about the new tenant. All fields are required before
            provisioning.
          </p>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Company name" error={errors.companyName?.message}>
            <input
              {...register('companyName')}
              placeholder="Name"
              className={inputCls}
            />
          </Field>
          <Field
            label="NetSuite Account ID"
            error={errors.netsuiteAccountId?.message}
          >
            <input
              {...register('netsuiteAccountId')}
              placeholder="NS-00000"
              className={inputCls}
            />
          </Field>
          <Field label="Primary contact" error={errors.primaryContact?.message}>
            <input
              {...register('primaryContact')}
              placeholder="Contact name"
              className={inputCls}
            />
          </Field>
          <Field label="Contact email" error={errors.contactEmail?.message}>
            <input
              {...register('contactEmail')}
              placeholder="contact@email.com"
              className={inputCls}
            />
          </Field>
        </div>

        <Field
          label="Plan tier"
          error={errors.planId?.message}
          className="mb-6"
        >
          <select {...register('planId')} className={inputCls}>
            <option value="">Select a plan…</option>
            {plans.map((p) => (
              <option
                key={p.id}
                value={p.id}
                className="bg-bg-elevated text-primary"
              >
                {p.name} — {(p.includedTokens / 1000).toFixed(0)}K tokens · $
                {p.platformAccessFee}/mo
              </option>
            ))}
          </select>
        </Field>

        {/* this field was just added by Melnard. This isn't included in the Wireframe */}
        <Field
          label="Credential mode"
          error={errors.credentialMode?.message}
          className="mb-6"
        >
          <select {...register('credentialMode')} className={inputCls}>
            <option value="" className="bg-bg-elevated text-primary">
              Select…
            </option>
            <option
              value="platform_managed"
              className="bg-bg-elevated text-primary"
            >
              Platform managed
            </option>
            <option value="byok" className="bg-bg-elevated text-primary">
              Bring your own key (BYOK)
            </option>
          </select>
        </Field>
      </div>

      <div className="flex justify-end border-t border-white/8 p-6">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Creating…' : 'Continue'}
          <ArrowRight size={14} />
        </Button>
      </div>
    </form>
  );
}

const inputCls =
  'w-full rounded border border-white/10 bg-white/4 px-3 py-2 text-sm text-primary placeholder:text-primary/20 focus:border-[--color-accent] focus:outline-none';

function Field({
  label,
  error,
  children,
  className = '',
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="text-muted mb-1 block text-xs font-medium">
        {label}
      </label>
      {children}
      {error && <p className="text-danger mt-1 text-xs">{error}</p>}
    </div>
  );
}
